import { ABNValidator } from "lib/validation";
import { PICValidator } from "lib/validation/validators";

import * as Yup from "yup";

export interface FieldConfiguration {
  value: string; // Reference that needs to be unique here.
  targetFieldName: string; // The field name on the business form.
  label: string; // A human readable label for the field.
  validation?: Yup.AnySchema | null; // A Yup Validation function that will be used to validate the field.
  businessValueGetter: (business: Business) => string; // A function that will be used to get the current value of the field from the business.
  sourceFieldSuggestions: string[]; // A list of field names from a CSV that will trigger this field as a default value in the source data.
  group?: string; // A string value that indicates the group of the field, used for display purposes.
  handleAsProperty?: boolean; // Properties are handled differently to generic fields.
  handleAsContact?: boolean; // So are contacts.
  index?: number; // A number value that indicates the index when used with contacts or pics.
  fieldName?: string; // A string value that indicates the field name on the business form.   Falls back to value in most cases.
}

const contactConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`contact_email_${index}`] = {
    value: `contact_email_${index}`,
    label: `Contact Email ${index}`,
    businessValueGetter: (business: Business) =>
      `${business.emailRecipients.length} existing contacts.`,
    sourceFieldSuggestions: [`email${index}`],
    index,
    fieldName: "email",
    handleAsContact: true,
    group: index === 1 ? "Contacts" : undefined,
  };
  acc[`contact_phone_${index}`] = {
    value: `contact_phone_${index}`,
    label: `Contact Phone ${index}`,
    businessValueGetter: (business: Business) =>
      `${business.emailRecipients.length} existing contacts.`,
    sourceFieldSuggestions: [`phone${index}`],
    index,
    fieldName: "phone",
    handleAsContact: true,
  };
  acc[`contact_first_name_${index}`] = {
    value: `contact_first_name_${index}`,
    label: `Contact First Name ${index}`,
    businessValueGetter: (business: Business) =>
      `${business.emailRecipients.length} existing contacts.`,
    sourceFieldSuggestions: [`firstname${index}`, "firstname"],
    index,
    fieldName: "firstName",
    handleAsContact: true,
  };
  acc[`contact_last_name_${index}`] = {
    value: `contact_last_name_${index}`,
    label: `Contact Last Name ${index}`,
    businessValueGetter: (business: Business) =>
      `${business.emailRecipients.length} existing contacts.`,
    sourceFieldSuggestions: [`lastname${index}`, "lastname"],
    index,
    fieldName: "lastName",
    handleAsContact: true,
  };
  return acc;
}, {});

const propertyConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`PIC_${index}`] = {
    value: `PIC_${index}`,
    label: `PIC ${index}`,
    businessValueGetter: (business: Business) =>
      `${business.properties.length} existing properties.`,
    sourceFieldSuggestions: index === 1 ? [`piccode`, "pic"] : [],
    handleAsProperty: true,
    index,
    group: index === 1 ? "Properties" : undefined,
    validation: PICValidator,
  };
  return acc;
}, {});

export const fieldConfigurations: Record<string, FieldConfiguration> = {
  // Simple "General Collapse" fields.
  NAME: {
    value: "name",
    targetFieldName: "name",
    label: "Name",
    validation: null,
    businessValueGetter: (business: Business) => business.name,
    sourceFieldSuggestions: ["name", "displayname", "tradingname"],
    group: "General",
  },
  PUBLIC_DISPLAY_NAME: {
    value: "publicDisplayName",
    targetFieldName: "publicDisplayName",
    label: "Public Display Name",
    validation: null,
    businessValueGetter: (business: Business) => business.publicDisplayName,
    sourceFieldSuggestions: [],
  },
  SHORT_CODE: {
    value: "shortCode",
    targetFieldName: "shortCode",
    label: "Agent Reference",
    validation: null,
    businessValueGetter: (business: Business) => business.shortCode,
    sourceFieldSuggestions: ["agentreference", "code", "agentcode", "agentref"],
  },
  SHORT_CODE_SALEYARD: {
    value: "shortCodeSaleyard",
    targetFieldName: "shortCodeSaleyard",
    label: "Saleyard Reference",
    validation: null,
    businessValueGetter: (business: Business) => business.shortCodeSaleyard,
    sourceFieldSuggestions: [
      "saleyardreference",
      "saleyardcode",
      "syref",
      "sycode",
    ],
  },
  SHORT_CODE_AUCTIONS_PLUS: {
    value: "shortCodeAuctionsPlus",
    targetFieldName: "shortCodeAuctionsPlus",
    label: "Auctions Plus Shortcode",
    validation: null,
    businessValueGetter: (business: Business) => business.shortCodeAuctionsPlus,
    sourceFieldSuggestions: [
      "auctionspshortcode",
      "auctionsplusshortcode",
      "auctionsplusshortcode",
      "auctionsplusshortcode",
    ],
  },
  NOTES: {
    value: "notes",
    targetFieldName: "notes",
    label: "Notes",
    validation: null,
    businessValueGetter: (business: Business) => business.notes,
    sourceFieldSuggestions: ["note", "notes"],
  },
  BRANDS: {
    value: "brands",
    targetFieldName: "brands",
    label: "Brands",
    validation: null,
    businessValueGetter: (business: Business) => business.brands,
    sourceFieldSuggestions: ["brand", "brands"],
  },

  // TODO booleans..
  // Address fields.
  ADDRESSS_STREET_NUMBER: {
    value: "addressStreetNumber",
    label: "Address Street Number",
    targetFieldName: "address.streetNumber",
    validation: null,
    businessValueGetter: () => "-",
    sourceFieldSuggestions: ["streetnumber", "addressstreetnumber"],
  },
  ADDRESSS_ROUTE: {
    value: "addressRoute",
    label: "Address Route",
    targetFieldName: "address.route",
    validation: null,
    businessValueGetter: () => "-",
    sourceFieldSuggestions: [
      "addressroute",
      "streetname",
      "street",
      "address1",
    ],
  },
  ADDRESSS_LOCALITY: {
    value: "addressLocality",
    label: "Address Locality",
    targetFieldName: "address.locality",
    validation: null,
    businessValueGetter: () => "-",
    sourceFieldSuggestions: ["locality", "town", "city", "suburb", "address2"],
  },
  ADDRESSS_STATE: {
    value: "addressState",
    label: "Address State",
    targetFieldName: "address.state",
    validation: null,
    businessValueGetter: () => "-",
    sourceFieldSuggestions: ["addressstate", "state", "statename"],
  },
  ADDRESSS_POSTAL_CODE: {
    value: "addressPostalCode",
    label: "Address Postal Code",
    targetFieldName: "address.postalCode",
    validation: null,
    businessValueGetter: () => "-",
    sourceFieldSuggestions: ["postcode", "postalcode", "physicalpostcode"],
  },
  // Financial fields.
  BSB: {
    value: "bankingDetailsBsb",
    label: "BSB",
    targetFieldName: "bankingDetails.bsb",
    validation: null,
    businessValueGetter: (business: Business) => business.bankingDetails?.bsb,
    sourceFieldSuggestions: ["bsb", "bsbnumber"],
    group: "Financial",
  },
  ACCOUNT_NUMBER: {
    value: "bankingDetailsAccountNumber",
    label: "Account Number",
    targetFieldName: "bankingDetails.accountNumber",
    validation: null,
    businessValueGetter: (business: Business) =>
      business.bankingDetails?.accountNumber,
    sourceFieldSuggestions: ["accountnumber", "account"],
  },

  ACCOUNT_NAME: {
    value: "bankingDetailsAccountName",
    label: "Account Name",
    targetFieldName: "bankingDetails.accountName",
    validation: null,
    businessValueGetter: (business: Business) =>
      business.bankingDetails?.accountName,
    sourceFieldSuggestions: ["accountname", "accname"],
  },

  ABN: {
    value: "abn",
    label: "ABN",
    targetFieldName: "abn",
    businessValueGetter: (business: Business) => business.abn,
    sourceFieldSuggestions: ["abn"],
    validation: ABNValidator,
  },

  // Enumerated fields - PICs
  ...propertyConfigurations,

  // Enumerated - Contacts
  ...contactConfigurations,
};
